import React from 'react';
import {useTranslation} from 'react-i18next';

import type {QuantityInputProps} from '@/components/ui/quantity-input';
import {QuantityInput} from '@/components/ui/quantity-input';
import {Typography} from '@/components/ui/typography';
import {graphqlClient} from '@/services/graphqlClient';
import {useGetWarehouseItemByIdQuery} from '@/services/graphqlHooks';

export type SharedStockInputProps = Omit<QuantityInputProps, 'min' | 'max'> & {
  warehouseId?: string;
  itemId?: string;
};

const SharedStockInput = React.forwardRef<
  HTMLInputElement,
  SharedStockInputProps
>(({warehouseId = '', itemId = '', value, ...inputProps}, ref) => {
  const {t} = useTranslation();
  const enabled = !!itemId && !!warehouseId;

  const {data: warehouseItemData, isFetched} = useGetWarehouseItemByIdQuery(
    graphqlClient,
    {warehouseId, id: itemId},
    {enabled}
  );

  const quantity = warehouseItemData?.getWarehouseItemById?.quantity ?? 0;
  const remaining = quantity - value;

  return (
    <div className="flex flex-col gap-2">
      <QuantityInput
        ref={ref}
        value={value}
        min={0}
        max={quantity}
        {...inputProps}
        disabled={!isFetched || inputProps.disabled}
      />

      <Typography asChild className="text-center text-sm text-muted-foreground">
        <span>
          {t('In stock')}: <b>{quantity}</b> | {t('Remaining')}:{' '}
          <b>{remaining}</b>
        </span>
      </Typography>
    </div>
  );
});

SharedStockInput.displayName = 'SharedStockInput';

export default SharedStockInput;
